/**
 * 단원평가 특강(오늘 수업 hard lock) — 학습자 API.
 * quizId 는 Snowflake → 항상 string.
 */
import { apiClient, ApiResponse } from './client'
import type { QuizScript } from '@quebon/shared-learning'

export type UaMode = 'PRINT' | 'ONLINE'
export type UaStatus =
  | 'PENDING'
  | 'STARTED'
  | 'SUBMITTED'
  | 'GRADED'
  | 'REVIEW_OPEN'
  | 'COMPLETED'
  | 'CANCELLED'

export interface SpecialLectureLockView {
  id: number
  mode: UaMode
  status: UaStatus
  setName: string
  anchorLessonId: number
  itemCount?: number
}

export interface LearnerUnitAssessmentItem {
  quizId: string
  sortOrder: number
  answered: boolean
  questionSnapshot: QuizScript | null
}

export interface LearnerUnitAssessmentView {
  id: number
  mode: UaMode
  status: UaStatus
  setName: string
  resumeFromIndex: number
  items: LearnerUnitAssessmentItem[]
}

export interface UnitAssessmentReviewItem {
  quizId: string
  sortOrder: number
  answer: string | null
  isCorrect: boolean | null
  corrects?: (boolean | null)[] | null
  videoWatched: boolean
  hasSolutionVideo: boolean
  correctAnswer?: unknown[] | null
  questionSnapshot: QuizScript | null
  explanation: string | null
}

export interface UnitAssessmentReviewView {
  id: number
  status: UaStatus
  score: number | null
  items: UnitAssessmentReviewItem[]
}

export function isUnitAssessmentNotFound(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { error?: { code?: string } } } })?.response
  return response?.status === 404 && response.data?.error?.code === 'NOT_FOUND'
}

/** active(미완료/미취소) 특강 조회 — 없으면 null. */
export async function getActiveSpecialLecture(): Promise<SpecialLectureLockView | null> {
  const res = await apiClient.get<ApiResponse<SpecialLectureLockView | null>>(
    '/lc/learner/me/special-lecture',
  )
  return res.data.data ?? null
}

export async function getSpecialLectureDetail(id: number): Promise<LearnerUnitAssessmentView> {
  const res = await apiClient.get<ApiResponse<LearnerUnitAssessmentView>>(
    `/lc/learner/me/special-lecture/${id}`,
  )
  return res.data.data
}

export async function saveOnlineAnswer(
  id: number,
  quizId: string,
  answers: unknown[],
): Promise<void> {
  await apiClient.post<ApiResponse<Record<string, boolean>>>(
    `/lc/learner/me/special-lecture/${id}/online/answer`,
    { quizId, answers },
  )
}

export async function submitOnline(id: number): Promise<SpecialLectureLockView> {
  const res = await apiClient.post<ApiResponse<SpecialLectureLockView>>(
    `/lc/learner/me/special-lecture/${id}/online/submit`,
  )
  return res.data.data
}

export async function getSpecialLectureReview(id: number): Promise<UnitAssessmentReviewView> {
  const res = await apiClient.get<ApiResponse<UnitAssessmentReviewView>>(
    `/lc/learner/me/special-lecture/${id}/review`,
  )
  return res.data.data
}

export async function checkReviewVideo(
  id: number,
  quizId: string,
  watched: boolean,
): Promise<void> {
  await apiClient.post<ApiResponse<Record<string, boolean>>>(
    `/lc/learner/me/special-lecture/${id}/review/video`,
    { quizId, watched },
  )
}
